import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Table, Input, Button, Alert } from 'antd';
import {
  CheckCircleOutlined,
  DollarOutlined,
  CalendarOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useUserOrders } from '../../../hooks/useUserOrders';
import { LoyaltyStatusCard, NotesCard } from '../../../components/user-detail';
import { formatVnd } from '../../../services/userManagementService';
import { formatVnDate, formatVnTime, vnDayjs } from '../../../utils/vnDateTime';
import { MerchantAvatar } from '../../../components/MerchantAvatar';

const ROUNDED = { borderRadius: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' };

interface OrderHistoryTabProps {
  userId: string;
  loyaltyBalance: number;
  loyaltyLifetime: number;
  loyaltyExpiring: number;
  loyaltyGoal?: number;
  noteValue: string;
  onNoteChange: (v: string) => void;
  onSaveNote?: () => void;
}

export const OrderHistoryTab: React.FC<OrderHistoryTabProps> = ({
  userId,
  loyaltyBalance,
  loyaltyLifetime,
  loyaltyExpiring,
  loyaltyGoal = 5000,
  noteValue,
  onNoteChange,
  onSaveNote,
}) => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const limit = 20;

  const {
    data,
    isLoading: loading,
    isError: error,
    refetch,
  } = useUserOrders(userId, { page, limit, search });

  const orders = data?.data ?? [];
  const pagination = data?.pagination;

  const formatLastOrder = (iso: string | null): string => {
    if (!iso) return '–';
    const d = vnDayjs(iso);
    const now = vnDayjs();
    if (d.isSame(now, 'day')) return 'Today';
    if (d.isSame(now.subtract(1, 'day'), 'day')) return 'Yesterday';
    return formatVnDate(iso, 'MMM D, YYYY');
  };

  const lastOrderAt =
    orders.length > 0
      ? orders.reduce((a, b) => {
          const aDate = a.created_at ?? a.createdAt ?? '';
          const bDate = b.created_at ?? b.createdAt ?? '';
          return aDate > bDate ? a : b;
        })
      : null;

  const avgSpending =
    orders.length > 0
      ? orders.reduce((s, o) => s + (o.total ?? o.totalAmount ?? 0), 0) /
        orders.length
      : null;
  const completedCount = orders.filter(
    o => o.status === 'delivered' || o.status === 'completed'
  ).length;
  const successRate =
    orders.length > 0
      ? Math.round((completedCount / orders.length) * 1000) / 10
      : null;

  const columns = [
    {
      title: 'ORDER ID',
      dataIndex: 'order_number',
      key: 'order_number',
      render: (_: string, r: (typeof orders)[0]) =>
        r.order_number || r.orderCode
          ? `#${r.order_number || r.orderCode}`
          : '–',
    },
    {
      title: 'MERCHANT',
      key: 'merchant',
      render: (_: unknown, r: (typeof orders)[0]) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <MerchantAvatar
            name={r.restaurantName ?? r.merchantName}
            imageUrl={r.restaurant_image ?? r.restaurantImage}
            size={32}
          />
          <span>{r.restaurantName ?? r.merchantName ?? '–'}</span>
        </div>
      ),
    },
    {
      title: 'DATE & TIME',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (v: string, r: (typeof orders)[0]) => {
        const dateStr = v ?? r.createdAt;
        if (!dateStr) return '–';
        return (
          <div>
            <div>{formatVnDate(dateStr, 'MMM D, YYYY')}</div>
            <div style={{ fontSize: 12, color: '#6b7280' }}>
              {formatVnTime(dateStr, 'hh:mm A')}
            </div>
          </div>
        );
      },
    },
    {
      title: 'TOTAL',
      dataIndex: 'total',
      key: 'total',
      render: (_: number, r: (typeof orders)[0]) =>
        formatVnd(r.total ?? r.totalAmount ?? 0),
    },
    {
      title: 'STATUS',
      dataIndex: 'status',
      key: 'status',
      render: (s: string) => {
        const isCompleted = s === 'delivered' || s === 'completed';
        return (
          <span
            style={{
              padding: '4px 10px',
              borderRadius: 9999,
              fontSize: 12,
              fontWeight: 500,
              background: isCompleted ? '#dcfce7' : '#dbeafe',
              color: isCompleted ? '#166534' : '#1d4ed8',
            }}
          >
            {isCompleted ? 'Completed' : 'Processing'}
          </span>
        );
      },
    },
    {
      title: 'ACTION',
      key: 'action',
      render: (_: unknown, r: (typeof orders)[0]) => (
        <Button
          type="link"
          style={{ padding: 0 }}
          onClick={() => navigate(`/orders/${r.id}`)}
        >
          View Details
        </Button>
      ),
    },
  ];

  if (error) {
    return (
      <div
        style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 24 }}
      >
        <Alert
          type="error"
          message="Không thể tải lịch sử đơn hàng."
          showIcon
          action={
            <Button size="small" onClick={() => refetch()}>
              Thử lại
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 24 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 24,
          }}
        >
          <Card style={ROUNDED} bodyStyle={{ padding: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 12,
                  background: '#dcfce7',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CheckCircleOutlined
                  style={{ fontSize: 20, color: '#16a34a' }}
                />
              </div>
              <div>
                <div style={{ fontSize: 13, color: '#6b7280' }}>
                  Success Rate
                </div>
                <div
                  style={{ fontSize: 22, fontWeight: 700, color: '#111827' }}
                >
                  {successRate != null ? `${successRate}%` : '–'}
                </div>
              </div>
            </div>
          </Card>
          <Card style={ROUNDED} bodyStyle={{ padding: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 12,
                  background: '#dcfce7',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <DollarOutlined style={{ fontSize: 20, color: '#16a34a' }} />
              </div>
              <div>
                <div style={{ fontSize: 13, color: '#6b7280' }}>
                  Avg. Spending
                </div>
                <div
                  style={{ fontSize: 22, fontWeight: 700, color: '#111827' }}
                >
                  {avgSpending != null ? formatVnd(avgSpending) : '–'}
                </div>
              </div>
            </div>
          </Card>
          <Card style={ROUNDED} bodyStyle={{ padding: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 12,
                  background: '#dbeafe',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CalendarOutlined style={{ fontSize: 20, color: '#2563eb' }} />
              </div>
              <div>
                <div style={{ fontSize: 13, color: '#6b7280' }}>Last Order</div>
                <div
                  style={{ fontSize: 22, fontWeight: 700, color: '#111827' }}
                >
                  {formatLastOrder(
                    lastOrderAt
                      ? lastOrderAt.created_at ?? lastOrderAt.createdAt ?? null
                      : null
                  )}
                </div>
              </div>
            </div>
          </Card>
        </div>

        <Card title="Recent Orders" style={ROUNDED}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 8,
              marginBottom: 16,
            }}
          >
            <Input
              placeholder="Search orders..."
              prefix={<SearchOutlined style={{ color: '#9ca3af' }} />}
              value={search}
              onChange={e => {
                setSearch(e.target.value);
                setPage(1);
              }}
              style={{ width: 240, borderRadius: 8 }}
            />
          </div>
          <Table
            loading={loading}
            dataSource={orders}
            rowKey={r => String(r.id)}
            pagination={{
              current: page,
              pageSize: limit,
              total: pagination?.total ?? 0,
              onChange: setPage,
              showSizeChanger: false,
            }}
            size="middle"
            columns={columns}
            locale={{ emptyText: 'No orders' }}
          />
        </Card>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        <LoyaltyStatusCard currentPoints={loyaltyBalance} goal={loyaltyGoal} />
        <Card style={ROUNDED}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#6b7280' }}>Current Balance</span>
              <span style={{ fontWeight: 600 }}>
                {loyaltyBalance.toLocaleString()} pts
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#6b7280' }}>Lifetime Earned</span>
              <span style={{ fontWeight: 600 }}>
                {loyaltyLifetime.toLocaleString()} pts
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#6b7280' }}>Expiring Soon</span>
              <span style={{ fontWeight: 600, color: '#dc2626' }}>
                {loyaltyExpiring} pts
              </span>
            </div>
          </div>
        </Card>
        <NotesCard
          value={noteValue}
          onChange={onNoteChange}
          onSave={onSaveNote}
          showSaveButton={false}
        />
      </div>
    </div>
  );
};
